<script setup lang="ts">
import { RefundRequestEmbed } from '@/Pejer/tables/RefundRequests'
import { SquareArrowOutUpRight } from 'lucide-vue-next'
import MoneyFormatted from 'pejer/src/components/MoneyFormatted.vue'
import { Badge } from 'pejer/src/components/ui/badge'
import { Button } from 'pejer/src/components/ui/button'

import { usePejer } from '@/Pejer'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import DialogFooter from 'pejer/src/components/ui/dialog/DialogFooter.vue'
import { Separator } from 'pejer/src/components/ui/separator'
import usePejerMutation from 'pejer/src/usePejerMutation'
import { ref } from 'vue'
import RefundReason from './RefundReason.vue'

const props = defineProps<{
  refundRequestItem: RefundRequestEmbed.ListItem
}>()

const modalOpen = ref(false)

const pejer = usePejer()
const { mutate, dataState } = usePejerMutation(
  pejer.urls.refundRequests.accept,
  {
    queryKeys: [['refundRequests']],
    onSuccess: () => {
      modalOpen.value = false
    },
  },
  {
    toastOnSuccess: true,
  }
)
</script>
<template>
  <span v-if="refundRequestItem.isAccepted">
    <Badge variant="outline" class="text-green-500">Gjennomført</Badge>
  </span>
  <Dialog
    v-else
    v-model:open="modalOpen"
    @open="() => (modalOpen = true)"
    @close="() => (modalOpen = false)"
  >
    <DialogTrigger as-child>
      <slot>
        <Button
          @click.prevent.stop
          variant="ghost"
          class="p-1 h-8 hover:bg-gray-200 rounded-sm group text-slate-700"
        >
          Start refundering
          <SquareArrowOutUpRight class="group-hover:scale-110 text-slate-500 ml-1" :size="16" />
        </Button>
      </slot>
    </DialogTrigger>
    <DialogContent class="max-w-[calc(100vw-20px)] max-h-[calc(100vh-50px)] flex flex-col">
      <DialogTitle class="text-lg font-semibold">Start refundering</DialogTitle>
      <DialogDescription class="text-sm text-slate-500 mb-4">
        Du har ikke en inngått en remitteringsavtale, og må derfor starte refunderingen manuelt fra
        Banken. Følg stegene under for å fullføre refunderingen. <br />
        <Separator class="mt-2" />
      </DialogDescription>
      <div class="text-sm mb-4 flex flex-col gap-1">
        <span>
          Betaling: <b>{{ props.refundRequestItem.payment.description }}</b>
        </span>
        <span>
          Kunde: <b>{{ props.refundRequestItem.customer.name }}</b>
        </span>
        <span>
          Årsak:
          <b><RefundReason :payoutReason="props.refundRequestItem.payoutReason" /></b>
        </span>
      </div>
      <ol>
        <li>
          1. Logg inn i nettbanken og gå til konto med kontonummer
          <Badge variant="outline" class="font-bold text-primary">{{
            props.refundRequestItem.fromBankAccountNumber
          }}</Badge
          >.
        </li>
        <li>
          2. Start en ny betaling til kontonummer
          <Badge variant="outline" class="font-bold text-primary">{{
            props.refundRequestItem.toBankAccountNumber
          }}</Badge>
          med et beløp på
          <Badge variant="outline" class="font-bold text-primary">
            <MoneyFormatted :amount="props.refundRequestItem.amount" one-size /></Badge
          >.
        </li>
        <li>
          3. I meldingsfeltet for betalingen, skriv inn
          <Badge variant="outline" class="font-bold text-primary">
            <RefundReason :payoutReason="props.refundRequestItem.payoutReason" />
          </Badge>
          eller en passende tekst som beskriver årsaken til refunderingen.
        </li>
        <li>
          4. Når betalingen er gjennomført i nettbanken, klikk på
          <Badge variant="outline" class="font-bold text-primary">Refundering utført</Badge>-knappen
          under for å oppdatere statusen på refunderingen.
        </li>
      </ol>
      <DialogFooter>
        <DialogClose as-child> <Button variant="ghost">Lukk</Button> </DialogClose>
        <DataStateButton
          :data-state="dataState"
          @click.prevent.stop="mutate({ pathArgs: { id: props.refundRequestItem.id } })"
        >
          Refundering utført
        </DataStateButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
